import { useState } from 'react'
import { useConfirmDelete } from '../hooks/useConfirmDelete'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { commesseApi, cantieriApi, rapportinoCommessaApi } from '../services/api'
import { Plus, Edit2, Trash2, MapPin, ChevronDown, ChevronUp, Briefcase } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'

const STATUS_CLS: Record<string, string> = {
  pianificata: 'bg-blue-100 text-blue-700',
  in_corso: 'bg-green-100 text-green-700',
  completata: 'bg-gray-100 text-gray-600',
  sospesa: 'bg-amber-100 text-amber-700',
}
const STATUS_LABEL: Record<string, string> = {
  pianificata: 'Pianificata',
  in_corso: 'In Corso',
  completata: 'Completata',
  sospesa: 'Sospesa',
}

interface Commessa {
  id: number
  numero_commessa: string
  nome: string
  descrizione?: string
  contract_value?: number
  status: string
  start_date: string
  end_date?: string
  contract_file_path?: string
  is_active: boolean
}

interface Cantiere {
  id: number
  commessa_id: number
  nome: string
  indirizzo?: string
  latitude: number
  longitude: number
  geofence_radius_meters: number
  is_active: boolean
}

interface RapportinoCommessaRow {
  id: number
  data: string
  tecnico_nome: string
  tecnico_cognome: string
  pdf_url: string
}

const emptyForm = {
  numero_commessa: '',
  nome: '',
  descrizione: '',
  start_date: new Date().toISOString().split('T')[0],
  end_date: '',
  contract_value: '',
  status: 'pianificata',
}

const emptyCantiere = {
  nome: '',
  indirizzo: '',
  latitude: '',
  longitude: '',
  geofence_radius_meters: '500',
}

export default function Commesse() {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const { user } = useAuth()
  const qc = useQueryClient()
  const canEdit = ['admin', 'superadmin', 'dispatcher'].includes(user?.role || '')

  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState(emptyForm)
  const [filterStatus, setFilterStatus] = useState('')
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [showCantiereForm, setShowCantiereForm] = useState<number | null>(null)
  const [cantiereForm, setCantiereForm] = useState(emptyCantiere)

  const { data: commesse = [], isLoading } = useQuery<Commessa[]>({
    queryKey: ['commesse', filterStatus],
    queryFn: () =>
      commesseApi.list({ status: filterStatus || undefined }).then(r => r.data),
  })

  const { data: rapportiniCommessa = [] } = useQuery<RapportinoCommessaRow[]>({
    queryKey: ['rapportini-commessa', expandedId],
    queryFn: () => commesseApi.rapportini(expandedId as number).then(r => r.data),
    enabled: expandedId !== null,
  })

  const handleDownloadRapportino = async (id: number) => {
    try {
      const res = await rapportinoCommessaApi.getPdf(id)
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `rapportino_${id}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Errore nel download del PDF')
    }
  }

  const { data: cantieriMap } = useQuery<Record<number, Cantiere[]>>({
    queryKey: ['cantieri-all'],
    queryFn: async () => {
      const res = await cantieriApi.list()
      const cantieri: Cantiere[] = res.data
      return cantieri.reduce((acc, c) => {
        if (!acc[c.commessa_id]) acc[c.commessa_id] = []
        acc[c.commessa_id].push(c)
        return acc
      }, {} as Record<number, Cantiere[]>)
    },
  })

  const createMut = useMutation({
    mutationFn: (data: typeof emptyForm) => commesseApi.create({
      ...data,
      contract_value: data.contract_value ? parseFloat(data.contract_value) : null,
      end_date: data.end_date || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commesse'] })
      toast.success('Commessa creata')
      setShowForm(false)
      setForm(emptyForm)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore creazione'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: typeof emptyForm }) =>
      commesseApi.update(id, {
        ...data,
        contract_value: data.contract_value ? parseFloat(data.contract_value) : null,
        end_date: data.end_date || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commesse'] })
      toast.success('Commessa aggiornata')
      setEditingId(null)
      setShowForm(false)
      setForm(emptyForm)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore aggiornamento'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => commesseApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commesse'] })
      toast.success('Commessa eliminata')
    },
    onError: () => toast.error('Errore eliminazione'),
  })

  const createCantiereMut = useMutation({
    mutationFn: ({ commessaId, data }: { commessaId: number; data: typeof emptyCantiere }) =>
      commesseApi.createCantiere(commessaId, {
        ...data,
        commessa_id: commessaId,
        latitude: parseFloat(data.latitude),
        longitude: parseFloat(data.longitude),
        geofence_radius_meters: parseInt(data.geofence_radius_meters),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cantieri-all'] })
      toast.success('Cantiere aggiunto')
      setShowCantiereForm(null)
      setCantiereForm(emptyCantiere)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore cantiere'),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingId) {
      updateMut.mutate({ id: editingId, data: form })
    } else {
      createMut.mutate(form)
    }
  }

  const handleEdit = (c: Commessa) => {
    setEditingId(c.id)
    setForm({
      numero_commessa: c.numero_commessa,
      nome: c.nome,
      descrizione: c.descrizione || '',
      start_date: c.start_date,
      end_date: c.end_date || '',
      contract_value: c.contract_value?.toString() || '',
      status: c.status,
    })
    setShowForm(true)
  }

  const handleDelete = (id: number, nome: string) => {
    confirmDelete(`Eliminare la commessa "${nome}"?`, () => deleteMut.mutate(id))
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {ConfirmModal}
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Briefcase size={28} className="text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-800">Commesse</h1>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="input text-sm py-1"
          >
            <option value="">Tutti gli stati</option>
            <option value="pianificata">Pianificata</option>
            <option value="in_corso">In Corso</option>
            <option value="completata">Completata</option>
            <option value="sospesa">Sospesa</option>
          </select>
          {canEdit && (
            <button
              onClick={() => { setEditingId(null); setForm(emptyForm); setShowForm(!showForm) }}
              className="btn-primary flex items-center gap-2"
            >
              <Plus size={16} /> Nuova Commessa
            </button>
          )}
        </div>
      </div>

      {/* Form creazione/modifica */}
      {showForm && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold mb-4">
            {editingId ? 'Modifica Commessa' : 'Nuova Commessa'}
          </h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Numero Commessa *</label>
              <input
                className="input w-full"
                value={form.numero_commessa}
                onChange={e => setForm(p => ({ ...p, numero_commessa: e.target.value }))}
                required
                placeholder="es. COMM-2026-001"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nome *</label>
              <input
                className="input w-full"
                value={form.nome}
                onChange={e => setForm(p => ({ ...p, nome: e.target.value }))}
                required
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Descrizione</label>
              <textarea
                className="input w-full h-20"
                value={form.descrizione}
                onChange={e => setForm(p => ({ ...p, descrizione: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Data Inizio *</label>
              <input
                type="date"
                className="input w-full"
                value={form.start_date}
                onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Data Fine</label>
              <input
                type="date"
                className="input w-full"
                value={form.end_date}
                onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Valore Contratto (€)</label>
              <input
                type="number"
                step="0.01"
                className="input w-full"
                value={form.contract_value}
                onChange={e => setForm(p => ({ ...p, contract_value: e.target.value }))}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Stato</label>
              <select
                className="input w-full"
                value={form.status}
                onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
              >
                <option value="pianificata">Pianificata</option>
                <option value="in_corso">In Corso</option>
                <option value="completata">Completata</option>
                <option value="sospesa">Sospesa</option>
              </select>
            </div>
            <div className="col-span-2 flex gap-3 justify-end">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => { setShowForm(false); setEditingId(null) }}
              >
                Annulla
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={createMut.isPending || updateMut.isPending}
              >
                {editingId ? 'Aggiorna' : 'Crea'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista commesse */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Caricamento...</div>
      ) : commesse.length === 0 ? (
        <div className="card text-center py-12 text-gray-500">
          Nessuna commessa trovata
        </div>
      ) : (
        <div className="space-y-3">
          {commesse.map(c => (
            <div key={c.id} className="card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <button
                    onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                  >
                    {expandedId === c.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm text-gray-500">{c.numero_commessa}</span>
                      <span className="font-semibold text-gray-800 truncate">{c.nome}</span>
                      <span className={`badge text-xs ${STATUS_CLS[c.status] || 'bg-gray-100 text-gray-600'}`}>
                        {STATUS_LABEL[c.status] || c.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                      <span>Inizio: {format(new Date(c.start_date), 'dd/MM/yyyy', { locale: it })}</span>
                      {c.end_date && (
                        <span>Fine: {format(new Date(c.end_date), 'dd/MM/yyyy', { locale: it })}</span>
                      )}
                      {c.contract_value && (
                        <span className="font-medium text-green-700">
                          €{c.contract_value.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    <button
                      onClick={() => handleEdit(c)}
                      className="p-1.5 hover:bg-blue-50 text-blue-600 rounded"
                      title="Modifica"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      onClick={() => handleDelete(c.id, c.nome)}
                      className="p-1.5 hover:bg-red-50 text-red-600 rounded"
                      title="Elimina"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                )}
              </div>

              {/* Sezione cantieri espansa */}
              {expandedId === c.id && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <MapPin size={14} /> Cantieri
                    </h3>
                    {canEdit && (
                      <button
                        onClick={() => setShowCantiereForm(showCantiereForm === c.id ? null : c.id)}
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <Plus size={12} /> Aggiungi
                      </button>
                    )}
                  </div>

                  {/* Form nuovo cantiere */}
                  {showCantiereForm === c.id && (
                    <form
                      onSubmit={e => { e.preventDefault(); createCantiereMut.mutate({ commessaId: c.id, data: cantiereForm }) }}
                      className="bg-gray-50 rounded-lg p-4 mb-3 grid grid-cols-2 gap-3"
                    >
                      <div className="col-span-2">
                        <input
                          className="input w-full text-sm"
                          placeholder="Nome cantiere *"
                          value={cantiereForm.nome}
                          onChange={e => setCantiereForm(p => ({ ...p, nome: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          className="input w-full text-sm"
                          placeholder="Indirizzo"
                          value={cantiereForm.indirizzo}
                          onChange={e => setCantiereForm(p => ({ ...p, indirizzo: e.target.value }))}
                        />
                      </div>
                      <div>
                        <input
                          type="number"
                          step="any"
                          className="input w-full text-sm"
                          placeholder="Latitudine *"
                          value={cantiereForm.latitude}
                          onChange={e => setCantiereForm(p => ({ ...p, latitude: e.target.value }))}
                          required
                        />
                      </div>
                      <div>
                        <input
                          type="number"
                          step="any"
                          className="input w-full text-sm"
                          placeholder="Longitudine *"
                          value={cantiereForm.longitude}
                          onChange={e => setCantiereForm(p => ({ ...p, longitude: e.target.value }))}
                          required
                        />
                      </div>
                      <div>
                        <input
                          type="number"
                          className="input w-full text-sm"
                          placeholder="Raggio geofence (m)"
                          value={cantiereForm.geofence_radius_meters}
                          onChange={e => setCantiereForm(p => ({ ...p, geofence_radius_meters: e.target.value }))}
                        />
                      </div>
                      <div className="flex gap-2 items-end">
                        <button type="submit" className="btn-primary text-sm flex-1" disabled={createCantiereMut.isPending}>
                          Salva
                        </button>
                        <button type="button" className="btn-secondary text-sm flex-1" onClick={() => setShowCantiereForm(null)}>
                          Annulla
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Lista cantieri */}
                  {(cantieriMap?.[c.id] || []).length === 0 ? (
                    <p className="text-sm text-gray-400 italic">Nessun cantiere associato</p>
                  ) : (
                    <div className="space-y-2">
                      {(cantieriMap?.[c.id] || []).map(cant => (
                        <div key={cant.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg text-sm">
                          <MapPin size={14} className="text-orange-500 flex-shrink-0" />
                          <div>
                            <span className="font-medium">{cant.nome}</span>
                            {cant.indirizzo && (
                              <span className="ml-2 text-gray-500">{cant.indirizzo}</span>
                            )}
                            <span className="ml-2 text-gray-400 text-xs">
                              ({cant.latitude.toFixed(5)}, {cant.longitude.toFixed(5)}) • raggio: {cant.geofence_radius_meters}m
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Rapportini</h4>
                    {rapportiniCommessa.length === 0 ? (
                      <p className="text-sm text-gray-400">Nessun rapportino registrato per questa commessa.</p>
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-500">
                            <th className="pb-1">Data</th>
                            <th className="pb-1">Tecnico</th>
                            <th className="pb-1">PDF</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rapportiniCommessa.map(r => (
                            <tr key={r.id} className="border-t border-gray-100">
                              <td className="py-1">{new Date(r.data).toLocaleDateString('it-IT')}</td>
                              <td className="py-1">{r.tecnico_nome} {r.tecnico_cognome}</td>
                              <td className="py-1">
                                <button
                                  type="button"
                                  onClick={() => handleDownloadRapportino(r.id)}
                                  className="text-blue-600 hover:underline"
                                >
                                  Scarica PDF
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
